import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { formatMonthYearHeader, getMonthsByYear, monthsEnum } from '../../../../../utils/dateUtils';

import notAvailable from '../../../../../assets/not-available.svg';
import greenTick from '../../../../../assets/available.svg';
import yellowTick from '../../../../../assets/yellow-tick.svg';
import hyphenGrey from '../../../../../assets/hyphen-grey.svg';
import styles from './scss/MonthlyView.module.scss';
import Tooltip from '../../../../../common/_custom/Tooltip/Tooltip';
import { fetchGstr3bData } from '../../../../../store/dashboard/revenueVendorExpenses/revenueVendorExpensesApiHelpers';

interface SmallMonthlyViewProps {
    height: any;
    error: any;
    width: any;
    graphData: any;
    graphName: string;
    isPDFReport?: boolean;
}

type FilingComplianceEntry = {
    gstin: string;
    filingStatus: 'Filed' | 'Delayed' | 'Default' | string;
    noOfDaysOfDelay: number;
    dateOfFiling?: string;
};

type DataSourceEntry = { gstNumber?: string; state?: string | null };

const FILING_GRAPH_NAMES = new Set(['gstr1FilingStatus', 'gstr3bFilingStatus']);

const dataSourceListCache = new Map<string, Promise<DataSourceEntry[]>>();

const GstHoverContent = ({
    entries,
    monthHeader,
    dataSourceList,
}: {
    entries: FilingComplianceEntry[];
    monthHeader: string;
    dataSourceList: DataSourceEntry[];
}) => (
    <div className={styles.Tooltip}>
        <div className={styles.TopText}>
            GST Returns Status <span className={styles.Divider}>|</span>{' '}
            <span className={styles.Month}>{formatMonthYearHeader(monthHeader)}</span>
        </div>
        <div className={styles.Gsts}>
            {entries.map((entry, i) => {
                const stateName = dataSourceList.find((d) => d.gstNumber === entry.gstin)?.state;
                const isDefault = entry.filingStatus === 'Default';
                return (
                    <div className={styles.GstRow} key={`${entry.gstin}-${i}`}>
                        <div className={styles.Left}>{stateName ? `${stateName} (${entry.gstin})` : entry.gstin}</div>
                        <div className={styles.Right}>
                            <div
                                className={clsx(styles.Badge, {
                                    [styles.Defaulted]: isDefault,
                                    [styles.Delayed]: !isDefault,
                                })}
                            >
                                {isDefault ? 'Default' : `${entry.noOfDaysOfDelay} ${entry.noOfDaysOfDelay === 1 ? 'Day' : 'Days'} Delay`}
                            </div>
                        </div>
                    </div>
                );
            })}
        </div>
    </div>
);

async function fetchDataSourceList(orgId: string): Promise<DataSourceEntry[]> {
    try {
        const data: any = await fetchGstr3bData({ orgId }, {});
        return data?.AION_GET_GST_LIST?.totalGstList ?? [];
    } catch {
        return [];
    }
}

function loadDataSourceList(orgId: string): Promise<DataSourceEntry[]> {
    if (dataSourceListCache.has(orgId)) {
        return dataSourceListCache.get(orgId)!;
    }

    const promise = fetchDataSourceList(orgId);
    dataSourceListCache.set(orgId, promise);
    return promise;
}

function MonthlyView({ height, graphData, graphName, isPDFReport = false }: SmallMonthlyViewProps) {
    const { months, datesObj, startDate } = getMonthsByYear(24);
    const plots = graphData?.plots?.[0]?.plotValues || [];
    let data = {};
    let delayDaysData = {};
    let entriesByXValue: Record<string, FilingComplianceEntry[]> = {};
    plots.forEach((plot) => {
        data[plot.xValue] = plot.yValue;
        delayDaysData[plot.xValue] = plot.yValue2;
        const filings = plot.extras?.filings as FilingComplianceEntry[] | undefined;
        if (filings && filings.length) entriesByXValue[plot.xValue] = filings;
    });

    const params = useParams();
    const orgId = params.id;
    const isFilingChart = FILING_GRAPH_NAMES.has(graphName);
    const [dataSourceList, setDataSourceList] = useState<DataSourceEntry[]>([]);

    useEffect(() => {
        if (!isFilingChart || !orgId || isPDFReport) return;
        let active = true;
        loadDataSourceList(orgId).then((list) => {
            if (active) setDataSourceList(list);
        });
        return () => {
            active = false;
        };
    }, [isFilingChart, orgId, isPDFReport]);

    const getDelayedOrDefaulted = (month: string, year: string): FilingComplianceEntry[] => {
        if (!isFilingChart) return [];
        return entriesByXValue[`${monthsEnum[month]?.name}_${year}`] || [];
    };

    const getAvailabilityStatusIcon = (month: string, year: string, data: any, ind, i) => {
        const date = `${monthsEnum[month].name}_${year}`;
        // if (start.isAfter(current)) return hyphenGrey;
        if (data?.[date]) {
            if (data?.[date] === 1) return greenTick;
            if (data?.[date] === 2) return yellowTick;
            if (data?.[date] === 3) return notAvailable;
        }
        return hyphenGrey;
    };

    const Legend = () => {
        const legendData = [
            { img: hyphenGrey, label: 'Data Unavailable' },
            { img: greenTick, label: 'On-Time' },
            { img: yellowTick, label: 'Delayed' },
            { img: notAvailable, label: 'Default' },
        ];
        return (
            <div
                className={clsx(styles.LegendContainer, {
                    [styles.PDFReportLegend]: isPDFReport,
                })}
            >
                {legendData.map((legend, i) => (
                    <div className={styles.Legend} key={i}>
                        <img src={legend.img} alt="yellow" height="10px" />
                        {legend.label}
                    </div>
                ))}
            </div>
        );
    };

    /**
     * Render the cell content. Delayed cells show a day-count badge; defaulted /
     * unavailable / on-time cells show their icon. When the parent chart is a
     * GST filing status chart and the month has any delayed/defaulted GSTINs,
     * wrap the cell in a tooltip listing them.
     */
    const renderCell = (month: string, year: string, ind: number, i: number) => {
        const date = `${monthsEnum[month].name}_${year}`;
        const status = data?.[date];
        const cellInner =
            status === 2 ? (
                <span className={styles.DelayCount}>{delayDaysData?.[date]}</span>
            ) : (
                <img src={getAvailabilityStatusIcon(month, year, data, ind, i)} alt="avl" height={isPDFReport ? '6px' : '9px'} />
            );

        if (!isFilingChart || isPDFReport) return cellInner;

        const entries = getDelayedOrDefaulted(month, year);
        if (!entries.length) return cellInner;

        const monthHeader = `${month}-${year}`;
        return (
            <Tooltip
                content={<GstHoverContent entries={entries} monthHeader={monthHeader} dataSourceList={dataSourceList} />}
                direction={'top'}
                usePortal={true}
                hideDelay={300}
            >
                {cellInner}
            </Tooltip>
        );
    };

    return (
        <div
            className={clsx(styles.SmallMonthlyView, {
                [styles.PDFReport]: isPDFReport,
            })}
            style={{
                height: isPDFReport ? 'auto' : height,
            }}
        >
            <Legend />
            {!isPDFReport && (
                <div className={styles.Top} style={{ margin: '10px 0' }}>
                    <div className={styles.TopLeft}>
                        <div className={styles.Row}></div>
                    </div>
                </div>
            )}

            <div className={clsx(styles.TableContainer)}>
                <table className={clsx(styles.Table)}>
                    <thead>
                        <tr>
                            <th className={styles.FirstColumn}> </th>
                            {months.map((month: any, i: number) => (
                                <th className={styles.MonthName} key={`${month}${i}`}>
                                    {monthsEnum[month]?.name}
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {Object.keys(datesObj).map((year, i) => (
                            <tr key={i}>
                                <td className={styles.Year}>{year}</td>
                                {datesObj[year].map((month: any, ind: any) => (
                                    <td key={ind} className={styles.Item}>
                                        <div className={styles.Img}>{renderCell(month, year, ind, i)}</div>
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default MonthlyView;
